import PropTypes from "prop-types";

// Layouts
import CustomizedSwitches from "@/Layouts/components/InputComponents/CustomizedSwitches";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// MUI
import { Stack, SvgIcon, Tooltip, Typography } from "@mui/material";

// Icons
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";

export default function MetricItem(props) {
	const {
		index = 0,
		item = null,
		tooltip = null,
		useLevels = false,
		options = [],
		optionsLevels = [],
		optionsLevelsDisabled = new Map(),
		loadingOptions = false,
		metricTitle = "Metric",
		requiredTitle = "Required",
		deleteTitle = "Remove Metric",
		headerMinHeight = 1,
		requiredMaxWidth = 65,
		error = false,
		levelError = false,
		readOnly = false,
		onActionChangeRequired = () => {},
		onActionRemove = () => {},
		onActionSelectLevel = () => {},
		onActionSelectMetric = () => {},
	} = props;

	return (
		<Stack direction={"row"} alignItems={"center"} spacing={1.5}>
			<Stack width={requiredMaxWidth}>
				<Stack sx={{ minHeight: index ? "auto" : headerMinHeight }}>
					<Typography className="draw-label" sx={{ textAlign: "left", display: index ? "none" : "inherit" }}>
						{requiredTitle}
					</Typography>
				</Stack>
				<Stack alignItems={"center"} minHeight={"30px"} pl={"5px"}>
					<CustomizedSwitches
						textRight
						checked={Boolean(item?.required)}
						disabled={readOnly || error}
						onChange={(value) => onActionChangeRequired?.(index, value)}
					/>
				</Stack>
			</Stack>

			<Stack direction={"row"} width={"290px"} spacing={2}>
				<Stack width={1}>
					<Stack width={1} ml={"-1px"} alignItems={"center"} sx={{ minHeight: index ? "auto" : headerMinHeight }}>
						<Typography className="draw-label" sx={{ display: index ? "none" : "inherit" }}>
							{metricTitle}
						</Typography>
					</Stack>

					<Stack direction={"row"} alignItems={"center"} width={1}>
						<Stack direction={"row"} alignItems={"center"} position={"relative"} width={1}>
							{Boolean(tooltip) && (
								<Stack position={"absolute"} ml={-3} sx={{ color: "var(--neutral-gray)" }}>
									<Tooltip title={tooltip} arrow>
										<InfoRoundedIcon fontSize={"small"} />
									</Tooltip>
								</Stack>
							)}

							<SmartSelector
								selectBy={"metricName"}
								showBy={"metricName"}
								options={options || []}
								selected={item?.metricName ? { metricName: item.metricName } : null}
								inputProps={{
									placeholder: "Select Metric",
								}}
								listBoxProps={{
									sx: { maxHeight: "195px" },
								}}
								clearable
								disabled={readOnly}
								error={error}
								loading={loadingOptions}
								sx={{ width: "100%" }}
								onChangeAction={(selected) => onActionSelectMetric?.(index, selected)}
							/>
						</Stack>
					</Stack>
				</Stack>

				{Boolean(useLevels) && (
					<Stack width={1} sx={{ maxWidth: "115px" }}>
						<Stack width={1} ml={"-1px"} alignItems={"center"} sx={{ minHeight: index ? "auto" : headerMinHeight }}>
							<Typography className="draw-label" sx={{ display: index ? "none" : "inherit" }}>
								Compliance Level
							</Typography>
						</Stack>

						<Stack direction={"row"} alignItems={"center"} width={1}>
							<Stack direction={"row"} alignItems={"center"} position={"relative"} width={1}>
								<SmartSelector
									selectBy={"level"}
									showBy={"level"}
									options={optionsLevels || []}
									disabledOptions={optionsLevelsDisabled?.get(item?.metricName) || []}
									selected={item?.metricLevel ? { level: item.metricLevel } : null}
									inputProps={{
										placeholder: "Select Level",
									}}
									listBoxProps={{
										sx: { maxHeight: "195px" },
									}}
									disabled={readOnly}
									error={levelError}
									loading={loadingOptions}
									sx={{ width: "100%" }}
									onChangeAction={(selected) => onActionSelectLevel?.(index, selected)}
								/>
							</Stack>
						</Stack>
					</Stack>
				)}
			</Stack>

			<Stack>
				<Stack ml={-3} width={1} alignItems={"center"} sx={{ minHeight: index ? "auto" : headerMinHeight }}>
					<Typography className="draw-label" sx={{ display: "none" }}>
						Remove
					</Typography>
				</Stack>

				<Stack ml={-1.7}>
					<DoubleCheckingButton
						title={deleteTitle}
						icon={<CloseRoundedIcon />}
						iconHover={<CloseRoundedIcon />}
						endIcon={<SvgIcon className="!text-[16px] ml-[4px]" component={TrashIcon} viewBox={"0 0 14 14"} />}
						mainButtonClasses={"btn-simple-remove btn-pack-remove"}
						mainSx={{ marginLeft: "10px" }}
						disabled={readOnly}
						confirmationSx={{ right: 0, left: "10px", width: "130px !important" }}
						onDeleteAction={() => onActionRemove?.(index, item)}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
}
MetricItem.propTypes = {
	index: PropTypes.number,
	item: PropTypes.object,
	tooltip: PropTypes.string,
	useLevels: PropTypes.bool,
	options: PropTypes.array,
	optionsLevels: PropTypes.array,
	optionsLevelsDisabled: PropTypes.instanceOf(Map),
	loadingOptions: PropTypes.bool,
	metricTitle: PropTypes.string,
	requiredTitle: PropTypes.string,
	deleteTitle: PropTypes.string,
	headerMinHeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
	requiredMaxWidth: PropTypes.number,
	error: PropTypes.bool,
	levelError: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionChangeRequired: PropTypes.func,
	onActionRemove: PropTypes.func,
	onActionSelectLevel: PropTypes.func,
	onActionSelectMetric: PropTypes.func,
};
